import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import RunwaysMap from "../../components/RunwaysMap/RunwaysMap";
import FlightRadar from "./fr24_logo.png";
import GoogleMaps from "./Google-Maps.png";

const initialAirportInfo = { googleMaps: "", flightRadar: "", wikipedia: "" };

export default function AirportRunwaysMap({ airport, activeRunwaysData }) {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState("");
  const [airportInfo, setAirportInfo] = useState(initialAirportInfo);

  useEffect(() => {
    const options = {
      method: "GET",
      url: `https://aerodatabox.p.rapidapi.com/airports/icao/${airport.icao}`,
      headers: {
        "X-RapidAPI-Host": "aerodatabox.p.rapidapi.com",
        "X-RapidAPI-Key": process.env.REACT_APP_RAPIDAPI_KEY || "",
      },
    };

    if (!process.env.REACT_APP_RAPIDAPI_KEY) {
      setAirportInfo(initialAirportInfo);
      return;
    }

    axios
      .request(options)
      .then((response) => {
        setAirportInfo(response.data?.urls ?? initialAirportInfo);
      })
      .catch(() => {
        setAirportInfo(initialAirportInfo);
      });
  }, [airport.icao]);

  async function copyToClipboard(e) {
    if (!airportInfo.flightRadar) return;
    try {
      await navigator.clipboard.writeText(airportInfo.flightRadar);
    } catch {
      // clipboard API unavailable — silent fail
    }
    setCopySuccess(t("map.copied"));
    e.currentTarget.focus();
  }

  return (
    <section className="mb-20 md:mb-32" aria-labelledby="runway-map-heading">
      <div className="text-center mb-8">
        <h2
          id="runway-map-heading"
          className="text-2xl md:text-4xl font-bold text-white mb-3"
        >
          {airport.icao} {t("airport.runwayMap")}
        </h2>
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          * {t("map.disclaimer")}
        </p>
      </div>

      {/* Runway SVG map */}
      <div className="glass-card rounded-card p-4 max-w-4xl mx-auto mb-8 shadow-card">
        <RunwaysMap
          runwaysData={airport.runways}
          activeRunwaysData={activeRunwaysData}
        />
      </div>

      {/* External links */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-6 flex-wrap">
        {airportInfo.googleMaps?.length > 0 && (
          <a
            className="flex flex-col items-center gap-2 group focus-visible:ring-2 focus-visible:ring-aviation-cyan rounded-lg p-2"
            href={airportInfo.googleMaps}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${airport.icao} — ${t("map.openGoogleMaps")}`}
          >
            <img
              className="w-40 rounded-2xl opacity-80 group-hover:opacity-100 transition-opacity duration-200"
              src={GoogleMaps}
              alt="Google Maps"
            />
            <span className="text-xs text-slate-500 group-hover:text-aviation-cyan transition-colors duration-200">
              {t("map.openGoogleMaps")}
            </span>
          </a>
        )}

        {airportInfo.flightRadar?.length > 0 && (
          <div className="flex flex-col items-center gap-3">
            <button
              className="flex flex-col items-center gap-2 group focus-visible:ring-2 focus-visible:ring-aviation-cyan rounded-lg p-2"
              onClick={copyToClipboard}
              aria-label={t("copyRadar")}
            >
              <img
                className="w-28 rounded-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-200"
                src={FlightRadar}
                alt="FlightRadar24"
              />
              <span className="text-xs text-slate-500 group-hover:text-aviation-cyan transition-colors duration-200 max-w-xs text-center">
                {t("copyRadar")}
              </span>
            </button>
            {copySuccess && (
              <p className="text-xs text-aviation-radarGreen font-semibold" role="status">
                ✓ {copySuccess}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Wikipedia iframe (if available) */}
      {airportInfo.wikipedia?.length > 0 && (
        <div className="mt-12">
          <iframe
            src={airportInfo.wikipedia}
            title={`${airport.icao} Wikipedia`}
            className="w-full rounded-card border border-white/10"
            style={{ height: "600px" }}
          />
        </div>
      )}
    </section>
  );
}
